import { PASTEL_LIGHTEN_FACTOR } from './constants';

/**
 * Tworzy ścieżkę krzywej Béziera między dwoma punktami.
 * Używane zarówno przez interpolację warstw jak i edycję soczewek.
 */
export const createBezierPath = (x1, y1, x2, y2, controlPoint) => {
  if (controlPoint) {
    const cp1x = x1 + (controlPoint.x - x1) * 0.5;
    const cp1y = y1 + (controlPoint.y - y1) * 0.5;
    const cp2x = x2 + (controlPoint.x - x2) * 0.5;
    const cp2y = y2 + (controlPoint.y - y2) * 0.5;
    return `M ${x1},${y1} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2},${y2}`;
  }
  return `M ${x1},${y1} L ${x2},${y2}`;
};

/**
 * Catmull-Rom spline → SVG cubic Bézier path.
 * Zamknięta krzywa (closed=true) przechodzi płynnie przez wszystkie punkty.
 * tension: 0 = ostre, 1 = bardzo gładkie (domyślnie 0.5)
 */
export const catmullRomToPath = (points, closed = true, tension = 0.5) => {
  if (!points || points.length < 3) {
    // Za mało punktów — fallback na polygon
    if (!points || points.length === 0) return '';
    return 'M ' + points.map(p => `${p.x},${p.y}`).join(' L ') + (closed ? ' Z' : '');
  }

  const n = points.length;
  const alpha = tension;

  // Dla zamkniętej krzywej: rozszerz tablicę o punkty cykliczne
  const pts = closed
    ? [points[n - 1], ...points, points[0], points[1]]
    : [points[0], ...points, points[n - 1]];

  let d = `M ${points[0].x},${points[0].y}`;

  for (let i = 1; i < pts.length - 2; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2];

    // Punkty kontrolne cubic Bézier z Catmull-Rom
    const cp1x = p1.x + (p2.x - p0.x) / (6 / alpha);
    const cp1y = p1.y + (p2.y - p0.y) / (6 / alpha);
    const cp2x = p2.x - (p3.x - p1.x) / (6 / alpha);
    const cp2y = p2.y - (p3.y - p1.y) / (6 / alpha);

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  if (closed) d += ' Z';
  return d;
};

/**
 * Generuje klucz połączenia między warstwami.
 */
export const getConnectionKey = (fromBoreholeId, toBoreholeId, layerName, position, connectionId) => {
  return `${fromBoreholeId}-${toBoreholeId}-${layerName}-${position}-${connectionId}`;
};

/**
 * Generuje klucz punktu dostosowania połączenia.
 */
export const getConnectionPointKey = (fromBoreholeId, toBoreholeId, layerName, isTop, isFromSide, connectionId) => {
  return `${fromBoreholeId}-${toBoreholeId}-${layerName}-${isTop ? 'top' : 'bottom'}-${isFromSide ? 'from' : 'to'}-${connectionId}`;
};

/**
 * Oblicza łączną miąższość warstw.
 */
export const getTotalThickness = (layers) => {
  return layers.reduce((sum, layer) => sum + parseFloat(layer.thickness || 0), 0);
};

/**
 * Konwertuje kolor hex na wersję pastelową (rozjaśnioną).
 * @param {string} hexColor - kolor w formacie hex (#RRGGBB)
 * @param {boolean} usePastel - czy zastosować rozjaśnienie
 */
export const toPastelColor = (hexColor, usePastel) => {
  if (!usePastel) return hexColor;

  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const pastelR = Math.round(r + (255 - r) * PASTEL_LIGHTEN_FACTOR);
  const pastelG = Math.round(g + (255 - g) * PASTEL_LIGHTEN_FACTOR);
  const pastelB = Math.round(b + (255 - b) * PASTEL_LIGHTEN_FACTOR);

  const toHex = (n) => {
    const h = n.toString(16);
    return h.length === 1 ? '0' + h : h;
  };

  return `#${toHex(pastelR)}${toHex(pastelG)}${toHex(pastelB)}`;
};

/**
 * Konwertuje kolor hex na wersję przyciemnioną (mnoży każdy kanał przez factor < 1).
 * Używane do wyostrzania konturów polygonów — kontur jest ciemniejszą wersją wypełnienia.
 * @param {string} hexColor - kolor w formacie hex (#RRGGBB)
 * @param {number} factor - 0..1, im niżej tym ciemniej (default 0.55)
 */
export const toDarkerColor = (hexColor, factor = 0.55) => {
  if (!hexColor || typeof hexColor !== 'string') return hexColor;
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) return hexColor;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkR = Math.max(0, Math.round(r * factor));
  const darkG = Math.max(0, Math.round(g * factor));
  const darkB = Math.max(0, Math.round(b * factor));

  const toHex = (n) => {
    const h = n.toString(16);
    return h.length === 1 ? '0' + h : h;
  };

  return `#${toHex(darkR)}${toHex(darkG)}${toHex(darkB)}`;
};
